// FolderNavigation.js - Folder drill-down navigation replacing left sidebar
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Platform,
  Animated,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

// Inject web hover styles once
if (Platform.OS === "web" && typeof document !== "undefined") {
  const STYLE_ID = "folder-nav-hover-styles";
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      [data-folder-card]:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 6px 20px rgba(37,99,235,0.1) !important;
        border-color: #bfdbfe !important;
      }
      [data-breadcrumb-link]:hover {
        color: #2563eb !important;
        background-color: #eff6ff !important;
        border-radius: 4px;
      }
    `;
    document.head.appendChild(style);
  }
}

// IGCSE level label overrides (from LeftSidebar.js)
const LEVEL_LABEL_OVERRIDES = { // BDD 03/18/2026
  igcse: { 10: "Cambridge O Level", 11: "Cambridge IGCSE", 12: "Cambridge International AS & A Level" },
  igsce: { 10: "Cambridge O Level", 11: "Cambridge IGCSE", 12: "Cambridge International AS & A Level" },
};

function isIGCSEDb(contentDb) {
  const db = String(contentDb || "").toLowerCase();
  return db.includes("igcse") || db.includes("igsce");
}

function getLevelHeaderLabel({ contentDb, grade, t }) {
  const g = Number(String(grade ?? "").trim());
  if (isIGCSEDb(contentDb)) {
    const override =
      LEVEL_LABEL_OVERRIDES.igcse[g] || LEVEL_LABEL_OVERRIDES.igsce[g];
    if (override) return override;
  }
  return `${t("Grade")} ${grade}`;
}

// Subject accent color map
const SUBJECT_COLORS = {
  chemistry: "#2563eb",
  biology: "#059669",
  physics: "#7c3aed",
  mathematics: "#d97706",
  math: "#d97706",
  maths: "#d97706",
};

// Subject icon map
const SUBJECT_ICONS = {
  chemistry: "atom",
  biology: "leaf",
  physics: "flash",
  mathematics: "calculator",
  math: "calculator",
  maths: "calculator",
};

function getSubjectColor(subjectName) {
  const key = String(subjectName || "").toLowerCase().trim();
  for (const [k, v] of Object.entries(SUBJECT_COLORS)) {
    if (key.includes(k)) return v;
  }
  return "#2563eb"; // default blue
}

function getSubjectIcon(subjectName) {
  const key = String(subjectName || "").toLowerCase().trim();
  for (const [k, v] of Object.entries(SUBJECT_ICONS)) {
    if (key.includes(k)) return v;
  }
  return "book-open-variant";
}

const FolderNavigation = ({
  isDarkMode,
  sortedCurriculum,
  hasMultipleGrades,
  selectedChapter,
  onChapterSelect,
  onDepthChange,
  labelForCurriculumNode,
  t,
  contentDb,
}) => {
  // navPath: [] = root, [levelId] = grade, [levelId, subjectId] = subject, [levelId, subjectId, chapterId] = chapter
  const [navPath, setNavPath] = useState([]);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  // Auto-set first path element for single grade
  useEffect(() => {
    if (
      !hasMultipleGrades &&
      sortedCurriculum &&
      sortedCurriculum.length > 0 &&
      navPath.length === 0
    ) {
      setNavPath([sortedCurriculum[0].id]);
    }
  }, [hasMultipleGrades, sortedCurriculum]);

  // Sync navPath when selectedChapter changes externally
  useEffect(() => {
    if (!selectedChapter) return;
    const { levelId, subjectId, chapterId } = selectedChapter;
    if (levelId && subjectId && chapterId) {
      setNavPath([levelId, subjectId, chapterId]);
    } else if (levelId && subjectId) {
      setNavPath([levelId, subjectId]);
    } else if (levelId) {
      setNavPath([levelId]);
    }
  }, [
    selectedChapter?.levelId,
    selectedChapter?.subjectId,
    selectedChapter?.chapterId,
  ]);

  // Animate on navPath change
  useEffect(() => {
    fadeAnim.setValue(0);
    slideAnim.setValue(10);
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 180,
        useNativeDriver: true,
      }),
    ]).start();
  }, [navPath.length]);

  // Resolve current data from navPath
  const curriculum = sortedCurriculum || [];
  const currentLevel =
    navPath.length >= 1
      ? curriculum.find((l) => l.id === navPath[0])
      : null;
  const currentSubject =
    navPath.length >= 2 && currentLevel
      ? (currentLevel.subjects || []).find((s) => s.id === navPath[1])
      : null;

  // Determine current view depth
  const depth = navPath.length;

  // Report depth to parent so it can hide/show topic cards
  useEffect(() => {
    if (onDepthChange) onDepthChange(depth);
  }, [depth]);

  // Breadcrumb navigation handler
  const navigateTo = (newPath) => {
    setNavPath(newPath);
  };

  // When chapter is selected (depth reaches 3), fire callback and return breadcrumb only
  useEffect(() => {
    if (navPath.length === 3 && currentLevel && currentSubject) {
      const chapter = (currentSubject.chapters || []).find(
        (c) => c.id === navPath[2]
      );
      if (chapter) {
        const subjectLabel = labelForCurriculumNode(currentSubject);
        const chapterLabel = labelForCurriculumNode(chapter);
        onChapterSelect({
          levelId: navPath[0],
          subjectId: navPath[1],
          subjectName: subjectLabel,
          chapterId: navPath[2],
          chapterName: chapterLabel,
        });
      }
    }
  }, [navPath[0], navPath[1], navPath[2]]);

  // Build breadcrumb items
  const buildBreadcrumbs = () => {
    const crumbs = [];
    // Root crumb
    crumbs.push({
      label: t("Curriculum"),
      path: hasMultipleGrades ? [] : navPath.length > 0 ? [navPath[0]] : [],
      isActive: depth === 0 || (!hasMultipleGrades && depth <= 1),
    });
    // Grade crumb
    if (hasMultipleGrades && navPath.length >= 1 && currentLevel) {
      crumbs[0].isActive = false;
      crumbs.push({
        label: getLevelHeaderLabel({
          contentDb,
          grade: currentLevel.grade,
          t,
        }),
        path: [navPath[0]],
        isActive: depth === 1,
      });
    }
    // Subject crumb
    if (navPath.length >= 2 && currentSubject) {
      crumbs.forEach((c) => (c.isActive = false));
      crumbs.push({
        label: labelForCurriculumNode(currentSubject),
        path: [navPath[0], navPath[1]],
        isActive: depth === 2,
      });
    }
    // Chapter crumb
    if (navPath.length >= 3 && currentSubject) {
      crumbs.forEach((c) => (c.isActive = false));
      const chapter = (currentSubject.chapters || []).find(
        (c) => c.id === navPath[2]
      );
      if (chapter) {
        crumbs.push({
          label: labelForCurriculumNode(chapter),
          path: [navPath[0], navPath[1], navPath[2]],
          isActive: true,
        });
      }
    }
    return crumbs;
  };

  // Hint text for right side of breadcrumb
  const getHintText = () => {
    if (depth === 0 || (!hasMultipleGrades && depth <= 1)) {
      return t("Select a subject");
    }
    if (depth === 1 && hasMultipleGrades) {
      return t("Select a subject");
    }
    if (depth === 2) {
      return t("Select a chapter");
    }
    return "";
  };

  const crumbs = buildBreadcrumbs();

  // Breadcrumb bar (always rendered)
  const breadcrumbBar = (
    <View
      style={[
        localStyles.breadcrumbBar,
        {
          backgroundColor: isDarkMode ? "#1e293b" : "#fff",
          borderColor: isDarkMode
            ? "rgba(148,163,184,0.15)"
            : "#e2e8f0",
        },
        Platform.OS === "web" && {
          boxShadow: isDarkMode
            ? "0 1px 4px rgba(0,0,0,0.2)"
            : "0 1px 4px rgba(0,0,0,0.04)",
        },
      ]}
    >
      <View style={localStyles.breadcrumbLeft}>
        {crumbs.map((crumb, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <Text
                style={[
                  localStyles.breadcrumbDivider,
                  { color: isDarkMode ? "#475569" : "#cbd5e1" },
                ]}
              >
                {" \u203A "}
              </Text>
            )}
            {crumb.isActive ? (
              <Text
                style={[
                  localStyles.breadcrumbActive,
                  { color: isDarkMode ? "#f1f5f9" : "#0f172a" },
                ]}
                numberOfLines={1}
              >
                {crumb.label}
              </Text>
            ) : (
              <TouchableOpacity
                onPress={() => navigateTo(crumb.path)}
                style={[
                  localStyles.breadcrumbTouchable,
                  Platform.OS === "web" && { cursor: "pointer" },
                ]}
                {...(Platform.OS === "web" ? { dataSet: { breadcrumbLink: "true" } } : {})}
              >
                <Text
                  style={[
                    localStyles.breadcrumbInactive,
                    { color: isDarkMode ? "#94a3b8" : "#94a3b8" },
                  ]}
                  numberOfLines={1}
                >
                  {crumb.label}
                </Text>
              </TouchableOpacity>
            )}
          </React.Fragment>
        ))}
      </View>
      {getHintText() ? (
        <Text
          style={[
            localStyles.breadcrumbHint,
            { color: isDarkMode ? "#64748b" : "#94a3b8" },
          ]}
        >
          {getHintText()}
        </Text>
      ) : null}
    </View>
  );

  // Level 2 (chapter selected) - return only breadcrumb bar
  if (depth >= 3) {
    return breadcrumbBar;
  }

  // Determine which items to show
  let items = [];
  let pageTitle = "";
  let showLevel = "subjects"; // or "chapters"

  if (!hasMultipleGrades && depth <= 1 && currentLevel) {
    // Single grade: show subjects directly
    showLevel = "subjects";
    items = currentLevel.subjects || [];
    pageTitle = getLevelHeaderLabel({
      contentDb,
      grade: currentLevel.grade,
      t,
    });
  } else if (hasMultipleGrades && depth === 0) {
    // Multi-grade root: show grade selection
    showLevel = "grades";
    items = curriculum;
  } else if (depth === 1 && currentLevel) {
    // Grade selected, show subjects
    showLevel = "subjects";
    items = currentLevel.subjects || [];
    pageTitle = getLevelHeaderLabel({
      contentDb,
      grade: currentLevel.grade,
      t,
    });
  } else if (depth === 2 && currentSubject) {
    // Subject selected, show chapters
    showLevel = "chapters";
    items = currentSubject.chapters || [];
  }

  return (
    <View style={localStyles.container}>
      {breadcrumbBar}

      <ScrollView
        contentContainerStyle={localStyles.scrollContent}
        showsVerticalScrollIndicator={true}
      >
        <Animated.View
          style={{
            opacity: fadeAnim,
            transform: [{ translateX: slideAnim }],
          }}
        >
          {/* Grade selection (multi-grade only) */}
          {showLevel === "grades" && (
            <View style={localStyles.gridContainer}>
              <Text
                style={[
                  localStyles.pageTitle,
                  { color: isDarkMode ? "#f1f5f9" : "#0f172a" },
                ]}
              >
                {t("Select a Grade")}
              </Text>
              <View style={localStyles.grid}>
                {items.map((level) => {
                  const label = getLevelHeaderLabel({
                    contentDb,
                    grade: level.grade,
                    t,
                  });
                  const subjectCount = (level.subjects || []).length;
                  return (
                    <TouchableOpacity
                      key={level.id}
                      onPress={() => navigateTo([level.id])}
                      activeOpacity={0.7}
                      style={[
                        localStyles.folderCard,
                        {
                          backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                          borderColor: isDarkMode
                            ? "rgba(148,163,184,0.15)"
                            : "#e2e8f0",
                        },
                        Platform.OS === "web" && localStyles.folderCardWeb,
                      ]}
                      {...(Platform.OS === "web" ? { dataSet: { folderCard: "true" } } : {})}
                    >
                      <View
                        style={[
                          localStyles.iconSquare,
                          {
                            backgroundColor: isDarkMode
                              ? "rgba(99,102,241,0.15)"
                              : "rgba(99,102,241,0.1)",
                          },
                        ]}
                      >
                        <MaterialCommunityIcons
                          name="school"
                          size={24}
                          color={isDarkMode ? "#818cf8" : "#6366f1"}
                        />
                      </View>
                      <View style={localStyles.cardTextContainer}>
                        <Text
                          style={[
                            localStyles.cardTitle,
                            { color: isDarkMode ? "#f1f5f9" : "#0f172a" },
                          ]}
                          numberOfLines={1}
                        >
                          {label}
                        </Text>
                        <Text
                          style={[
                            localStyles.cardSubtitle,
                            { color: isDarkMode ? "#64748b" : "#94a3b8" },
                          ]}
                        >
                          {subjectCount}{" "}
                          {subjectCount === 1 ? t("subject") : t("subjects")}
                        </Text>
                      </View>
                      <MaterialCommunityIcons
                        name="chevron-right"
                        size={20}
                        color={isDarkMode ? "#475569" : "#cbd5e1"}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}

          {/* Subject selection */}
          {showLevel === "subjects" && (
            <View style={localStyles.gridContainer}>
              <Text
                style={[
                  localStyles.pageTitle,
                  { color: isDarkMode ? "#f1f5f9" : "#0f172a" },
                ]}
              >
                {pageTitle}
              </Text>
              <View style={localStyles.grid}>
                {items.map((subject) => {
                  const label = labelForCurriculumNode(subject);
                  const chapterCount = (subject.chapters || []).length;
                  const accentColor = getSubjectColor(label);
                  const iconName = getSubjectIcon(label);
                  return (
                    <TouchableOpacity
                      key={subject.id}
                      onPress={() =>
                        navigateTo(
                          currentLevel
                            ? [currentLevel.id, subject.id]
                            : [navPath[0], subject.id]
                        )
                      }
                      activeOpacity={0.7}
                      style={[
                        localStyles.folderCard,
                        {
                          backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                          borderColor: isDarkMode
                            ? "rgba(148,163,184,0.15)"
                            : "#e2e8f0",
                        },
                        Platform.OS === "web" && localStyles.folderCardWeb,
                      ]}
                      {...(Platform.OS === "web" ? { dataSet: { folderCard: "true" } } : {})}
                    >
                      <View
                        style={[
                          localStyles.iconSquare,
                          {
                            backgroundColor:
                              accentColor + (isDarkMode ? "26" : "26"),
                          },
                        ]}
                      >
                        <MaterialCommunityIcons
                          name={iconName}
                          size={24}
                          color={accentColor}
                        />
                      </View>
                      <View style={localStyles.cardTextContainer}>
                        <Text
                          style={[
                            localStyles.cardTitle,
                            { color: isDarkMode ? "#f1f5f9" : "#0f172a" },
                          ]}
                          numberOfLines={1}
                        >
                          {label}
                        </Text>
                        <Text
                          style={[
                            localStyles.cardSubtitle,
                            { color: isDarkMode ? "#64748b" : "#94a3b8" },
                          ]}
                        >
                          {chapterCount}{" "}
                          {chapterCount === 1 ? t("chapter") : t("chapters")}
                        </Text>
                      </View>
                      <MaterialCommunityIcons
                        name="chevron-right"
                        size={20}
                        color={isDarkMode ? "#475569" : "#cbd5e1"}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}

          {/* Chapter selection */}
          {showLevel === "chapters" && currentSubject && (
            <View style={localStyles.gridContainer}>
              {/* Subject header */}
              <View style={localStyles.chapterHeader}>
                <View
                  style={[
                    localStyles.chapterHeaderIcon,
                    {
                      backgroundColor:
                        getSubjectColor(
                          labelForCurriculumNode(currentSubject)
                        ) + "26",
                    },
                  ]}
                >
                  <MaterialCommunityIcons
                    name={getSubjectIcon(
                      labelForCurriculumNode(currentSubject)
                    )}
                    size={18}
                    color={getSubjectColor(
                      labelForCurriculumNode(currentSubject)
                    )}
                  />
                </View>
                <Text
                  style={[
                    localStyles.chapterHeaderText,
                    { color: isDarkMode ? "#f1f5f9" : "#0f172a" },
                  ]}
                >
                  {labelForCurriculumNode(currentSubject)}
                </Text>
              </View>

              <View style={localStyles.grid}>
                {items.map((chapter, index) => {
                  const label = labelForCurriculumNode(chapter);
                  const topicCount = (chapter.topics || []).length;
                  const chapterNum = String(index + 1).padStart(2, "0");
                  return (
                    <TouchableOpacity
                      key={chapter.id}
                      onPress={() =>
                        navigateTo([navPath[0], navPath[1], chapter.id])
                      }
                      activeOpacity={0.7}
                      style={[
                        localStyles.folderCard,
                        {
                          backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                          borderColor: isDarkMode
                            ? "rgba(148,163,184,0.15)"
                            : "#e2e8f0",
                        },
                        Platform.OS === "web" && localStyles.folderCardWeb,
                      ]}
                      {...(Platform.OS === "web" ? { dataSet: { folderCard: "true" } } : {})}
                    >
                      <View
                        style={[
                          localStyles.chapterBadge,
                          {
                            backgroundColor: isDarkMode
                              ? "rgba(37,99,235,0.15)"
                              : "#eff6ff",
                            borderColor: isDarkMode
                              ? "rgba(37,99,235,0.25)"
                              : "#dbeafe",
                          },
                        ]}
                      >
                        <Text
                          style={[
                            localStyles.chapterBadgeText,
                            {
                              color: isDarkMode ? "#60a5fa" : "#2563eb",
                            },
                          ]}
                        >
                          {chapterNum}
                        </Text>
                      </View>
                      <View style={localStyles.cardTextContainer}>
                        <Text
                          style={[
                            localStyles.cardTitle,
                            { color: isDarkMode ? "#f1f5f9" : "#0f172a" },
                          ]}
                          numberOfLines={2}
                        >
                          {label}
                        </Text>
                        {topicCount > 0 && (
                          <Text
                            style={[
                              localStyles.cardSubtitle,
                              { color: isDarkMode ? "#64748b" : "#94a3b8" },
                            ]}
                          >
                            {topicCount}{" "}
                            {topicCount === 1 ? t("topic") : t("topics")}
                          </Text>
                        )}
                      </View>
                      <MaterialCommunityIcons
                        name="chevron-right"
                        size={20}
                        color={isDarkMode ? "#475569" : "#cbd5e1"}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}

          {/* Empty state */}
          {items.length === 0 && (
            <View
              style={[
                localStyles.emptyState,
                {
                  backgroundColor: isDarkMode
                    ? "rgba(148,163,184,0.06)"
                    : "rgba(148,163,184,0.06)",
                  borderColor: isDarkMode
                    ? "rgba(148,163,184,0.1)"
                    : "rgba(148,163,184,0.12)",
                },
              ]}
            >
              <MaterialCommunityIcons
                name="folder-open-outline"
                size={32}
                color={isDarkMode ? "#475569" : "#cbd5e1"}
                style={{ marginBottom: 8 }}
              />
              <Text
                style={[
                  localStyles.emptyText,
                  { color: isDarkMode ? "#94a3b8" : "#64748b" },
                ]}
              >
                {t("No content available")}
              </Text>
            </View>
          )}
        </Animated.View>
      </ScrollView>
    </View>
  );
};

const localStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  // Breadcrumb bar
  breadcrumbBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderWidth: 1,
    borderRadius: 12,
    marginHorizontal: 28,
    marginTop: 12,
    marginBottom: 8,
  },
  breadcrumbLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
  breadcrumbDivider: {
    fontSize: 14,
    marginHorizontal: 2,
  },
  breadcrumbActive: {
    fontSize: 13,
    fontWeight: "600",
  },
  breadcrumbTouchable: {
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 4,
  },
  breadcrumbInactive: {
    fontSize: 13,
    fontWeight: "400",
  },
  breadcrumbHint: {
    fontSize: 12,
    fontWeight: "400",
    fontStyle: "italic",
    marginLeft: 12,
    flexShrink: 0,
  },
  // Scroll content
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
  // Grid layout
  gridContainer: {
    maxWidth: 680,
    width: "100%",
    alignSelf: "center",
    paddingHorizontal: 16,
  },
  pageTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginTop: 12,
    marginBottom: 16,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  // Folder card
  folderCard: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 14,
    paddingVertical: 18,
    paddingHorizontal: 20,
    width: "100%",
    ...(Platform.OS === "web"
      ? { flexBasis: "calc(50% - 6px)", maxWidth: "calc(50% - 6px)" }
      : {}),
  },
  folderCardWeb: {
    transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
    cursor: "pointer",
  },
  // Icon square (subject)
  iconSquare: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 14,
    flexShrink: 0,
  },
  // Chapter badge
  chapterBadge: {
    width: 40,
    height: 40,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 14,
    flexShrink: 0,
  },
  chapterBadgeText: {
    fontSize: 14,
    fontWeight: "700",
  },
  // Card text
  cardTextContainer: {
    flex: 1,
    minWidth: 0,
    marginRight: 8,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: "600",
    lineHeight: 20,
  },
  cardSubtitle: {
    fontSize: 12,
    fontWeight: "400",
    marginTop: 2,
  },
  // Chapter header
  chapterHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
    marginBottom: 16,
  },
  chapterHeaderIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  chapterHeaderText: {
    fontSize: 18,
    fontWeight: "700",
  },
  // Empty state
  emptyState: {
    padding: 32,
    marginHorizontal: 16,
    marginTop: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "dashed",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: 14,
    fontWeight: "500",
  },
});

export default FolderNavigation;
